import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, Search, ChevronRight, ArrowLeft, Clock } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo, { SITE_URL } from '../components/Seo';
import PaywallGate from '../components/PaywallGate';
import { ARTICLES, CATEGORIES } from './HelpPage';

const TOTAL_ARTICLES = Object.keys(ARTICLES).length;

function PublicArticleView({ slug }) {
  const article = ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0a0b0f] text-white">
        <Seo
          title="Article not found"
          description="The article you were looking for does not exist on haythix."
          path={`/docs/${slug}`}
          noIndex
        />
        <PublicNav />
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl font-bold mb-3">Article not found</h1>
          <p className="text-gray-400 mb-6">The slug “{slug}” does not match any haythix documentation article.</p>
          <Link to="/docs" className="text-amber-400 hover:text-amber-300 font-semibold inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to docs index
          </Link>
        </div>
      </div>
    );
  }

  const description = (article.what || article.intro || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/docs/${slug}` },
      headline: article.title,
      description,
      author: { '@type': 'Organization', name: 'haythix' },
      publisher: {
        '@type': 'Organization',
        name: 'haythix',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.png` },
      },
      isAccessibleForFree: false,
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: false,
        cssSelector: '.haythix-paywalled-body',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Documentation', item: `${SITE_URL}/docs` },
        { '@type': 'ListItem', position: 3, name: article.title,   item: `${SITE_URL}/docs/${slug}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={article.title}
        description={description}
        path={`/docs/${slug}`}
        jsonLd={jsonLd}
        type="article"
      />
      <PublicNav />

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/docs" className="text-xs text-gray-500 hover:text-amber-400 inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to all documentation
        </Link>

        <header className="mb-8 border-b border-dark-700/50 pb-6">
          <p className="text-xs uppercase tracking-widest text-amber-400/80 mb-2">{article.category}</p>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-3">{article.title}</h1>
          {article.readTime && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" /> {article.readTime} read
            </div>
          )}
        </header>

        {/* `.haythix-paywalled-body` matches the JSON-LD hasPart cssSelector */}
        <article className="haythix-paywalled-body space-y-8">
          <PaywallGate kind="article">
            {article.what && (
              <section>
                <h2 className="text-xl font-semibold mb-3 text-white">What it is</h2>
                <p className="text-gray-300 leading-relaxed">{article.what}</p>
              </section>
            )}

            {article.intro && (
              <section>
                <h2 className="text-xl font-semibold mb-3 text-white">How to think about it</h2>
                <p className="text-gray-300 leading-relaxed">{article.intro}</p>
              </section>
            )}

            {Array.isArray(article.steps) && article.steps.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 text-white">Step-by-step</h2>
                <ol className="space-y-4">
                  {article.steps.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                        <span className="text-sm font-bold text-amber-400">{i + 1}</span>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-white mb-1">{step.title}</h3>
                        <p className="text-sm text-gray-300 leading-relaxed">{step.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {Array.isArray(article.tips) && article.tips.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-3 text-white">Tips & pitfalls</h2>
                <ul className="space-y-2">
                  {article.tips.map((tip, i) => (
                    <li key={i} className="text-sm text-gray-300 leading-relaxed pl-4 border-l-2 border-amber-500/30">
                      {tip}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </PaywallGate>
        </article>
      </main>
      <PublicFooter />
    </div>
  );
}

function PublicDocsIndex() {
  const [search, setSearch] = useState('');

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const [slug, article] of Object.entries(ARTICLES)) {
      if (search) {
        const hay = `${article.title} ${article.category} ${article.what || ''}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) continue;
      }
      const cat = article.category || 'Other';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat).push({ slug, ...article });
    }
    return [...groups.entries()].map(([cat, items]) => ({ category: cat, items: items.sort((a, b) => a.title.localeCompare(b.title)) }));
  }, [search]);

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title={`haythix Documentation — ${TOTAL_ARTICLES} guides for crypto traders`}
        description={`Full documentation for haythix: ${TOTAL_ARTICLES} articles covering DEX + CEX trading bots, copy trading, AI signals, cross-chain arbitrage, tokenomics, taxes, and more.`}
        path="/docs"
      />
      <PublicNav />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <header className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 mb-4">
            <BookOpen className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-4xl font-bold mb-3">haythix Documentation</h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            {TOTAL_ARTICLES} articles for active crypto traders, DeFi power users, copy traders, and token issuers.
          </p>
        </header>

        <div className="relative max-w-xl mx-auto mb-10">
          <Search className="absolute top-1/2 left-4 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles…"
            className="w-full bg-dark-800 border border-dark-700 rounded-2xl pl-11 pr-4 py-3 text-sm text-white placeholder-gray-500 focus:border-amber-400 focus:outline-none"
          />
        </div>

        <div className="space-y-10">
          {grouped.map(({ category, items }) => (
            <section key={category}>
              <h2 className="text-xs uppercase tracking-widest text-amber-400/80 mb-4">{category}</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {items.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/docs/${a.slug}`}
                    className="group flex items-start justify-between gap-3 p-4 rounded-xl border border-dark-700/50 bg-dark-800/40 hover:border-amber-500/30 hover:bg-dark-800 transition-all"
                  >
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-white group-hover:text-amber-400 transition-colors mb-1">
                        {a.title}
                      </h3>
                      {a.readTime && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {a.readTime}
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 transition-colors flex-shrink-0 mt-0.5" />
                  </Link>
                ))}
              </div>
            </section>
          ))}

          {grouped.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              No articles match “{search}”.
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

export default function PublicDocsPage() {
  const { slug } = useParams();
  return slug ? <PublicArticleView slug={slug} /> : <PublicDocsIndex />;
}
